import { useCallback, useEffect, useRef } from 'react';
import { useIncomingPayment } from '@tectonics/prs/incoming-payment';
import type { IncomingPayment, OpenedIncomingPayment } from '@tectonics/prs/incoming-payment';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useFormFeedback } from '@tectonics/components/form/hooks/use-form-feedback';

const LOCAL_VALIDATION_ERROR_IDS: readonly string[] = ['empty', 'invalid-pattern'];

type ClearSecurityCode = (value: string) => void;

function isRetryableDecline(payment: IncomingPayment): payment is OpenedIncomingPayment {
  return payment.status === 'OP' && payment.retry === true;
}

interface PaymentAttempt {
  readonly paymentBefore: IncomingPayment | null;
}

const INITIAL_LOAD: PaymentAttempt = { paymentBefore: null };

function useClearOnRetryableDecline(isFeatureEnabled: boolean, clear: () => void): void {
  const payment = useIncomingPayment();
  const submitting = useSubmitting();
  const pendingAttempt = useRef<PaymentAttempt | null>(INITIAL_LOAD);

  useEffect(() => {
    if (submitting) {
      pendingAttempt.current ??= { paymentBefore: payment };
      return;
    }

    const attempt = pendingAttempt.current;

    if (attempt == null) return;

    pendingAttempt.current = null;

    const isNewDecline = payment !== attempt.paymentBefore && isRetryableDecline(payment);

    if (isFeatureEnabled && isNewDecline) clear();
  }, [submitting, payment, isFeatureEnabled, clear]);
}

function usePaymentWasRejected(isFeatureEnabled: boolean): boolean {
  const { formHasBeenSent, validationError, submitFailed } = useFormFeedback('cardSecurityCode');

  const hasNonLocalValidationError = Boolean(
    formHasBeenSent && validationError && !LOCAL_VALIDATION_ERROR_IDS.includes(validationError.id)
  );

  return isFeatureEnabled && (submitFailed || hasNonLocalValidationError);
}

function useClearOnRejection(isFeatureEnabled: boolean, clear: () => void): void {
  const rejected = usePaymentWasRejected(isFeatureEnabled);
  const wasRejectedRef = useRef(false);

  useEffect(() => {
    const isNewRejection = rejected && !wasRejectedRef.current;
    wasRejectedRef.current = rejected;

    if (!isNewRejection) return;

    clear();
  }, [rejected, clear]);
}

function useClearSecurityCodeAfterFailedAttempt(isFeatureEnabled: boolean, setValue: ClearSecurityCode): void {
  const clear = useCallback((): void => setValue(''), [setValue]);

  useClearOnRetryableDecline(isFeatureEnabled, clear);
  useClearOnRejection(isFeatureEnabled, clear);
}

export { useClearSecurityCodeAfterFailedAttempt };
